<template>
  <div class="event-map-outer">

    <div v-if="event.name" class="event-info-static compact-event-info">
      <div class="event-info-row">
        <div class="event-name-main compact-event-name">{{ event.name }}</div>
        <router-link
          v-if="$route.params.slug"
          :to="{ name: 'eventDashboard', params: { slug: $route.params.slug } }"
          class="dashboard-link-btn compact-dashboard-btn"
          aria-label="Go to Event Dashboard"
        >
          <font-awesome-icon :icon="['fas', 'tachometer-alt']" class="dashboard-icon" />
        </router-link>
      </div>
      <div v-if="event.from && event.to" class="event-date-main compact-event-date">{{ formatEventDate(event.from, event.to) }}</div>
    </div>
    <div v-if="loading" class="loading-spinner overlay-center">
      <font-awesome-icon icon="running" class="fa-3x running-animate" />
      <div class="mt-2">{{$t('eventMap.loadingMap') || 'Loading map...'}}</div>
    </div>
    <div v-else-if="error" class="error-message overlay-center">
      <span>{{ error }}</span>
    </div>
    <div v-else>
      <!-- Floating event name label -->
      <div class="mapbox-map-wrapper">
        <div ref="mapContainer" class="mapbox-map fade-in"></div>
        <div class="map-controls">
          <button
            v-if="polylines.length"
            class="fit-to-route-btn"
            @click="fitToRoute"
            title="Fit to Route"
            aria-label="Fit to Route"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style="margin-right:0.5em;" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="white" stroke-width="2" fill="none"/>
              <circle cx="12" cy="12" r="4" fill="white" fill-opacity="0.7"/>
              <circle cx="12" cy="12" r="2" fill="#357abd"/>
            </svg>
            <span>Fit to Route</span>
          </button>
          <button
            v-if="polylines.length"
            class="heatmap-toggle-btn"
            @click="toggleHeatmap"
            :title="showHeatmap ? 'Show Routes' : 'Show Heat Map'"
            :aria-label="showHeatmap ? 'Show Routes' : 'Show Heat Map'"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style="margin-right:0.5em;" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="white" stroke-width="2" fill="none"/>
              <circle cx="6" cy="6" r="3" fill="white" fill-opacity="0.8"/>
              <circle cx="18" cy="8" r="2" fill="white" fill-opacity="0.6"/>
              <circle cx="8" cy="18" r="2.5" fill="white" fill-opacity="0.7"/>
            </svg>
            <span>{{ showHeatmap ? 'Routes' : 'Heat Map' }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl'
import $backend from '../backend'
import polyline from 'polyline-encoded'

export default {
  name: 'EventMap',
  // Add metaInfo for browser tab title if using vue-meta
  metaInfo () {
    return {
      title: this.event && this.event.name ? `Event Map - ${this.event.name}` : 'Event Map'
    }
  },
  data () {
    return {
      loading: true,
      error: '',
      map: null,
      polylines: [],
      bounds: null,
      event: {},
      showHeatmap: true,
      heatmapData: [],
      routeLayers: [],
      heatmapLayer: null,
      impulseInterval: null,
      impulsePhase: 0
    }
  },
  mounted () {
    this.fetchPolylines()
  },
  watch: {
    'event.name' (val) {
      if (val) {
        document.title = `Event Map - ${val}`
      }
    }
  },
  beforeDestroy () {
    this.stopImpulseEffect()
    if (this.map) {
      this.map.remove()
    }
  },
  methods: {
    async fetchPolylines () {
      this.loading = true
      this.error = ''
      const slug = this.$route.params.slug
      try {
        const resp = await $backend.getEventPolylines(slug)
        if (resp && resp.data && resp.data.event && Array.isArray(resp.data.polylines)) {
          this.event = resp.data.event || {}
          this.polylines = resp.data.polylines
          if (!this.polylines.length) {
            this.error = 'No map data available.'
            this.loading = false
            // Do NOT call initMap if error is set
          } else {
            this.error = '' // clear any previous error
            this.loading = false
            this.$nextTick(() => {
              // Ensure DOM is ready and container exists
              if (this.$refs.mapContainer) {
                this.initMap()
              } else {
                // Wait a bit more for DOM to be ready
                setTimeout(() => {
                  if (this.$refs.mapContainer) {
                    this.initMap()
                  } else {
                    this.error = 'Map container not available.'
                  }
                }, 100)
              }
            })
          }
        } else if (resp && resp.msg) {
          this.error = resp.msg
          this.loading = false
        } else {
          this.error = 'No map data available.'
          this.loading = false
        }
      } catch (e) {
        // Check if it's an unauthorized error - axios interceptor will handle redirect
        if (e && e.data && e.data.msg === 'unauthorized') {
          // Don't set error message here, let the interceptor handle the redirect
          return
        }
        this.error = (e && e.data && e.data.msg)
          ? e.data.msg
          : 'Failed to load map data.'
        this.loading = false
      }
    },
    initMap () {
      if (!this.polylines.length) {
        this.error = 'No map data available.'
        return
      }

      // Check if map container exists
      if (!this.$refs.mapContainer) {
        this.error = 'Map container not found.'
        return
      }

      mapboxgl.accessToken = process.env.VUE_APP_MAPBOX_TOKEN || 'pk.eyJ1IjoibG9uZ2thaHQiLCJhIjoiY21kZ3dzazFuMDVmdDJtczZqajc0Nzg4dSJ9.kf9p-1HcWap-2k1aVuexyQ'
      if (!mapboxgl.accessToken) {
        this.error = 'Mapbox access token is not set.'
        return
      }

      try {
        this.map = new mapboxgl.Map({
          container: this.$refs.mapContainer,
          style: 'mapbox://styles/mapbox/streets-v11',
          center: [105.85, 21.03],
          zoom: 10
        })
      } catch (error) {
        this.error = 'Failed to initialize map: ' + error.message
        return
      }

      // Ensure map is properly initialized
      if (!this.map) {
        this.error = 'Map initialization failed.'
        return
      }

      this.map.addControl(new mapboxgl.NavigationControl())
      this.map.on('load', () => {
        let bounds = new mapboxgl.LngLatBounds()
        // Define a palette of visually appealing colors
        const routeColors = [
          '#4a90e2', // blue
          '#50e3c2', // teal
          '#f5a623', // orange
          '#b620e0', // purple
          '#e94e77', // pink
          '#7ed957', // green
          '#f8e71c' // yellow
        ]
        this.polylines.forEach((polylineData, idx) => {
          // Handle both old format (string) and new format (object with athlete_id)
          const encoded = typeof polylineData === 'string' ? polylineData : polylineData.polyline
          const athleteId = typeof polylineData === 'string' ? null : polylineData.athlete_id

          const coords = polyline.decode(encoded).map(([lat, lng]) => [lng, lat])
          coords.forEach(coord => bounds.extend(coord))

          this.map.addSource(`route${idx}`, {
            type: 'geojson',
            data: {
              type: 'Feature',
              geometry: {
                type: 'LineString',
                coordinates: coords
              },
              properties: {
                athlete_id: athleteId
              }
            }
          })
          this.map.addLayer({
            id: `route${idx}`,
            type: 'line',
            source: `route${idx}`,
            layout: {
              'line-join': 'round',
              'line-cap': 'round'
            },
            paint: {
              'line-color': routeColors[idx % routeColors.length],
              'line-width': 7,
              'line-opacity': 0.92
            },
            filter: ['==', '$type', 'LineString']
          })
          this.routeLayers.push(`route${idx}`)

          // Add hover layer for better interaction
          this.map.addLayer({
            id: `route${idx}-hover`,
            type: 'line',
            source: `route${idx}`,
            layout: {
              'line-join': 'round',
              'line-cap': 'round'
            },
            paint: {
              'line-color': routeColors[idx % routeColors.length],
              'line-width': 12,
              'line-opacity': 0
            },
            filter: ['==', '$type', 'LineString']
          })
        })

        // Generate heatmap data
        this.generateHeatmapData()

        // Add heatmap layer
        if (this.heatmapData.length > 0) {
          this.map.addSource('heatmap-points', {
            type: 'geojson',
            data: {
              type: 'FeatureCollection',
              features: this.heatmapData
            }
          })

          this.map.addLayer({
            id: 'heatmap-layer',
            type: 'heatmap',
            source: 'heatmap-points',
            paint: {
              'heatmap-weight': [
                'interpolate',
                ['linear'],
                ['get', 'weight'],
                0, 0,
                1, 1
              ],
              'heatmap-intensity': [
                'interpolate',
                ['linear'],
                ['zoom'],
                0, 1.5,
                9, 4
              ],
              'heatmap-color': [
                'interpolate',
                ['linear'],
                ['heatmap-density'],
                0, 'rgba(255, 165, 0, 0)',
                0.2, 'rgba(255, 165, 0, 0.2)',
                0.4, 'rgba(255, 140, 0, 0.4)',
                0.6, 'rgba(255, 120, 0, 0.6)',
                0.8, 'rgba(255, 100, 0, 0.8)',
                1, 'rgba(255, 80, 0, 0.95)'
              ],
              'heatmap-radius': [
                'interpolate',
                ['linear'],
                ['zoom'],
                0, 2,
                9, 20
              ],
              'heatmap-opacity': [
                'interpolate',
                ['linear'],
                ['zoom'],
                0, 0.6,
                9, 0.9
              ]
            }
          })
          this.heatmapLayer = 'heatmap-layer'

          // Initially show heatmap layer (default view)
          this.map.setLayoutProperty('heatmap-layer', 'visibility', 'visible')

          // Start impulse effect for default heatmap view
          this.startImpulseEffect()

          // Initially hide route layers (show heatmap by default)
          this.routeLayers.forEach(layerId => {
            if (this.map.getLayer(layerId)) {
              this.map.setLayoutProperty(layerId, 'visibility', 'none')
            }
          })
        }
        this.bounds = bounds
        // Add bounding box highlight
        if (!bounds.isEmpty()) {
          const bboxCoords = [
            [bounds.getSouthWest().lng, bounds.getSouthWest().lat],
            [bounds.getNorthWest().lng, bounds.getNorthWest().lat],
            [bounds.getNorthEast().lng, bounds.getNorthEast().lat],
            [bounds.getSouthEast().lng, bounds.getSouthEast().lat],
            [bounds.getSouthWest().lng, bounds.getSouthWest().lat]
          ]
          this.map.addSource('route-bbox', {
            type: 'geojson',
            data: {
              type: 'Feature',
              geometry: {
                type: 'Polygon',
                coordinates: [bboxCoords]
              }
            }
          })
          this.map.addLayer({
            id: 'route-bbox-fill',
            type: 'fill',
            source: 'route-bbox',
            layout: {},
            paint: {
              'fill-color': '#C5D5E4',
              'fill-opacity': 0.13
            }
          })
          this.map.addLayer({
            id: 'route-bbox-outline',
            type: 'line',
            source: 'route-bbox',
            layout: {},
            paint: {
              'line-color': '#C5D5E4',
              'line-width': 2.5,
              'line-opacity': 0.7
            }
          })
          this.map.fitBounds(bounds, { padding: 60, maxZoom: 16 })
        }

        // --- Add Hoang Sa (Paracel) and Truong Sa (Spratly) Islands markers and labels ---
        // Polygons will be loaded from an external JSON file
        let islands = []
        fetch('/vietnam_islands_polygons.json')
          .then(res => res.json())
          .then(data => {
            islands = data
            // Add text labels as symbol layer
            this.map.addSource('vietnam-islands-labels', {
              type: 'geojson',
              data: {
                type: 'FeatureCollection',
                features: islands.map(island => ({
                  type: 'Feature',
                  geometry: { type: 'Point', coordinates: island.coordinates },
                  properties: { name: island.name }
                }))
              }
            })
            this.map.addLayer({
              id: 'vietnam-islands-labels',
              type: 'symbol',
              source: 'vietnam-islands-labels',
              layout: {
                'text-field': ['get', 'name'],
                'text-size': 9, // keep small size
                'text-offset': [-1, 0],
                'text-anchor': 'top',
                'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular']
              },
              paint: {
                'text-color': '#234',
                'text-halo-color': '#fff',
                'text-halo-width': 2
              }
            })
            // Add polygons for the islands (support MultiPolygon)
            this.map.addSource('vietnam-islands-polygons', {
              type: 'geojson',
              data: {
                type: 'FeatureCollection',
                features: islands.flatMap(island =>
                  island.polygons.map(polygon => ({
                    type: 'Feature',
                    geometry: { type: 'Polygon', coordinates: [polygon] },
                    properties: { name: island.name }
                  }))
                )
              }
            })
            // Fill layer
            this.map.addLayer({
              id: 'vietnam-islands-polygons-fill',
              type: 'fill',
              source: 'vietnam-islands-polygons',
              layout: {},
              paint: {
                'fill-color': '#EFE9E1',
                'fill-opacity': 0.18
              }
            })
            // Outline layer
            this.map.addLayer({
              id: 'vietnam-islands-polygons-outline',
              type: 'line',
              source: 'vietnam-islands-polygons',
              layout: {},
              paint: {
                'line-color': '#EFE9E1', // changed to a grey rock color for a more natural, less constraining outline
                'line-width': 2.5,
                'line-opacity': 0.7
              }
            })
          })
        // --- End islands ---

        // Add mouse event handlers for polyline hover
        this.addPolylineHoverHandlers()
      })
    },
    addPolylineHoverHandlers () {
      // Create a popup but don't add it to the DOM yet
      const popup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        className: 'polyline-popup'
      })

      // Create a custom tooltip element
      const tooltip = document.createElement('div')
      tooltip.className = 'custom-tooltip'
      tooltip.style.display = 'none'
      tooltip.style.position = 'absolute'
      tooltip.style.zIndex = '1000'
      tooltip.style.backgroundColor = 'rgba(0, 0, 0, 0.8)'
      tooltip.style.color = 'white'
      tooltip.style.padding = '8px 12px'
      tooltip.style.borderRadius = '4px'
      tooltip.style.fontSize = '12px'
      tooltip.style.fontWeight = '500'
      tooltip.style.pointerEvents = 'none'
      tooltip.style.whiteSpace = 'nowrap'
      tooltip.style.maxWidth = '200px'
      tooltip.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.3)'

      document.body.appendChild(tooltip)

      // Function to check if zoom level is appropriate for hover
      const isZoomLevelAppropriate = () => {
        return this.map.getZoom() >= 12 // Only show hover when zoomed in enough
      }

      // Add hover handlers for each route layer
      this.routeLayers.forEach((layerId, idx) => {
        // Change the cursor to a pointer when the mouse is over the layer
        this.map.on('mouseenter', layerId, () => {
          if (!isZoomLevelAppropriate()) return

          this.map.getCanvas().style.cursor = 'pointer'

          // Show hover effect
          if (this.map.getLayer(`${layerId}-hover`)) {
            this.map.setPaintProperty(`${layerId}-hover`, 'line-opacity', 0.3)
          }
        })

        this.map.on('mouseleave', layerId, () => {
          this.map.getCanvas().style.cursor = ''

          // Hide hover effect
          if (this.map.getLayer(`${layerId}-hover`)) {
            this.map.setPaintProperty(`${layerId}-hover`, 'line-opacity', 0)
          }
        })

        // Show popup on hover
        this.map.on('mouseenter', layerId, (e) => {
          if (!isZoomLevelAppropriate()) return

          if (e.features.length > 0) {
            const feature = e.features[0]
            const athleteId = feature.properties.athlete_id

            if (athleteId) {
              const coordinates = e.lngLat

              // Show popup positioned next to the route
              popup
                .setLngLat(coordinates)
                .setHTML(`<div class="athlete-popup">
                  <strong>Athlete ID:</strong> ${athleteId}
                </div>`)
                .addTo(this.map)

              // Show custom tooltip positioned next to mouse
              tooltip.textContent = `Athlete ID: ${athleteId}`
              tooltip.style.display = 'block'

              // Position tooltip next to mouse position
              tooltip.style.left = (e.point.x + 10) + 'px'
              tooltip.style.top = (e.point.y - 30) + 'px'
            }
          }
        })

        this.map.on('mouseleave', layerId, () => {
          popup.remove()
          tooltip.style.display = 'none'
        })
      })

      // Also add hover handlers for hover layers as backup
      this.routeLayers.forEach((layerId, idx) => {
        const hoverLayerId = `${layerId}-hover`

        this.map.on('mouseenter', hoverLayerId, (e) => {
          if (!isZoomLevelAppropriate()) return

          this.map.getCanvas().style.cursor = 'pointer'

          if (e.features.length > 0) {
            const feature = e.features[0]
            const athleteId = feature.properties.athlete_id

            if (athleteId) {
              const coordinates = e.lngLat

              // Show popup positioned next to the route
              popup
                .setLngLat(coordinates)
                .setHTML(`<div class="athlete-popup">
                  <strong>Athlete ID:</strong> ${athleteId}
                </div>`)
                .addTo(this.map)

              // Show custom tooltip positioned next to mouse
              tooltip.textContent = `Athlete ID: ${athleteId}`
              tooltip.style.display = 'block'

              // Position tooltip next to mouse position
              tooltip.style.left = (e.point.x + 10) + 'px'
              tooltip.style.top = (e.point.y - 30) + 'px'
            }
          }
        })

        this.map.on('mouseleave', hoverLayerId, () => {
          this.map.getCanvas().style.cursor = ''
          popup.remove()
          tooltip.style.display = 'none'
        })
      })

      // Hide tooltips when zooming out
      this.map.on('zoom', () => {
        if (!isZoomLevelAppropriate()) {
          popup.remove()
          tooltip.style.display = 'none'
          this.map.getCanvas().style.cursor = ''
        }
      })
    },
    fitToRoute () {
      if (this.map && this.bounds && !this.bounds.isEmpty()) {
        this.map.fitBounds(this.bounds, { padding: 60, maxZoom: 16 })
      }
    },
    toggleHeatmap () {
      this.showHeatmap = !this.showHeatmap
      this.updateMapLayers()

      // Start/stop impulse effect based on heatmap visibility
      if (this.showHeatmap) {
        this.startImpulseEffect()
      } else {
        this.stopImpulseEffect()
      }
    },
    updateMapLayers () {
      if (!this.map) return

      if (this.showHeatmap) {
        // Hide route layers
        this.routeLayers.forEach(layerId => {
          if (this.map.getLayer(layerId)) {
            this.map.setLayoutProperty(layerId, 'visibility', 'none')
          }
        })

        // Show heatmap layer
        if (this.heatmapLayer && this.map.getLayer(this.heatmapLayer)) {
          this.map.setLayoutProperty(this.heatmapLayer, 'visibility', 'visible')
          this.startImpulseEffect()
        }
      } else {
        // Show route layers
        this.routeLayers.forEach(layerId => {
          if (this.map.getLayer(layerId)) {
            this.map.setLayoutProperty(layerId, 'visibility', 'visible')
          }
        })

        // Hide heatmap layer
        if (this.heatmapLayer && this.map.getLayer(this.heatmapLayer)) {
          this.map.setLayoutProperty(this.heatmapLayer, 'visibility', 'none')
          this.stopImpulseEffect()
        }
      }
    },
    generateHeatmapData () {
      const heatmapPoints = []

      this.polylines.forEach((polylineData, idx) => {
        // Handle both old format (string) and new format (object with athlete_id)
        const encoded = typeof polylineData === 'string' ? polylineData : polylineData.polyline
        const athleteId = typeof polylineData === 'string' ? null : polylineData.athlete_id

        const coords = polyline.decode(encoded).map(([lat, lng]) => [lng, lat])

        // Add points along the polyline for heatmap
        coords.forEach((coord, pointIdx) => {
          // Weight decreases as we go further along the route
          const weight = Math.max(0.1, 1 - (pointIdx / coords.length) * 0.5)
          heatmapPoints.push({
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: coord
            },
            properties: {
              weight: weight,
              athlete_id: athleteId
            }
          })
        })
      })

      this.heatmapData = heatmapPoints
    },
    startImpulseEffect () {
      if (this.impulseInterval) {
        clearInterval(this.impulseInterval)
      }

      this.impulseInterval = setInterval(() => {
        this.impulsePhase = (this.impulsePhase + 0.1) % (2 * Math.PI)
        this.updateImpulseEffect()
      }, 100) // Update every 100ms for smooth animation
    },
    stopImpulseEffect () {
      if (this.impulseInterval) {
        clearInterval(this.impulseInterval)
        this.impulseInterval = null
      }
    },
    updateImpulseEffect () {
      if (!this.map || !this.heatmapLayer || !this.map.getLayer(this.heatmapLayer)) {
        return
      }

      // Create pulsing effect by modulating the heatmap intensity
      const pulseIntensity = 1 + 0.3 * Math.sin(this.impulsePhase)

      this.map.setPaintProperty(this.heatmapLayer, 'heatmap-intensity', [
        'interpolate',
        ['linear'],
        ['zoom'],
        0, 1.5 * pulseIntensity,
        9, 4 * pulseIntensity
      ])

      // Also pulse the radius slightly
      const pulseRadius = 1 + 0.1 * Math.sin(this.impulsePhase * 2)
      this.map.setPaintProperty(this.heatmapLayer, 'heatmap-radius', [
        'interpolate',
        ['linear'],
        ['zoom'],
        0, 2 * pulseRadius,
        9, 20 * pulseRadius
      ])
    },
    formatEventDate (from, to) {
      if (!from || !to) return ''
      const fromDate = new Date(from * 1000)
      const toDate = new Date(to * 1000)
      const options = { year: 'numeric', month: 'short', day: 'numeric' }
      return `${fromDate.toLocaleDateString(undefined, options)} - ${toDate.toLocaleDateString(undefined, options)}`
    }
  }
}
</script>

<style scoped>
.event-map-outer {
  width: 100%;
  min-height: 100vh;
  background: #f8f9fa;
  padding: 0;
  margin: 0;
  position: relative;
  overflow: hidden;
}
.event-info-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  margin: 2rem auto 1.5rem auto;
  max-width: 700px;
  padding: 1.5rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  animation: fadeIn 0.7s;
}
.event-info-content {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  width: 100%;
}
.event-image {
  width: 90px;
  height: 90px;
  object-fit: cover;
  border-radius: 12px;
  border: 2px solid #e9ecef;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}
.event-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.event-title {
  font-size: 2rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
}
.event-date {
  color: #4a90e2;
  font-size: 1.1rem;
  font-weight: 500;
}
.event-description {
  color: #555;
  font-size: 1rem;
  margin-top: 0.3rem;
}
.back-btn {
  background: none;
  border: none;
  color: #4a90e2;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.7rem;
  cursor: pointer;
  transition: color 0.2s;
  padding: 0;
}
.back-btn:hover {
  color: #357abd;
}
.mapbox-map {
  width: 100%;
  height: 70vh;
  min-height: 350px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  margin: 0 auto 2.5rem auto;
  max-width: 1000px;
  background: #e9ecef;
  animation: fadeIn 1s;
  position: relative;
  z-index: 1;
  overflow: visible;
}
.fade-in {
  animation: fadeIn 1s;
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: none; }
}
.loading-spinner, .error-message {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 80vh;
  font-size: 1.3em;
  color: #4a90e2;
}
.overlay-info {
  position: absolute;
  bottom: 2.5vw;
  right: 2vw;
  z-index: 10;
  max-width: 420px;
  min-width: 220px;
  background: rgba(255,255,255,0.96);
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.13);
  padding: 1.2rem 1.5rem;
  animation: fadeIn 0.7s;
}
.overlay-controls {
  position: absolute;
  top: 2vw;
  right: 2vw;
  z-index: 10;
  display: flex;
  gap: 1rem;
  background: rgba(255,255,255,0.92);
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.10);
  padding: 0.7rem 1.1rem;
  align-items: center;
  animation: fadeIn 0.7s;
}
.overlay-center {
  position: absolute;
  top: 0; left: 0; width: 100%; height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  background: rgba(255,255,255,0.85);
}
.event-info-static {
  width: 100%;
  max-width: 700px;
  margin: 2.2rem auto 1.2rem auto;
  background: linear-gradient(135deg, #fafdff 0%, #e3f0fc 100%);
  color: #234;
  border-radius: 18px;
  box-shadow: 0 6px 24px rgba(74,144,226,0.08);
  border: 1.5px solid #e3eefe;
  padding: 1.5em 2.7em 1.5em 2.2em;
  display: flex;
  flex-direction: column;
  gap: 0.3em;
  align-items: flex-start;
  animation: fadeIn 1.2s;
  font-family: inherit;
  backdrop-filter: blur(2px);
}
.event-name-main {
  font-size: 1.3rem;
  font-weight: 800;
  letter-spacing: 0.01em;
  margin-bottom: 0.1em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.event-date-main {
  font-size: 0.95rem;
  font-weight: 500;
  color: #b0c4de;
  margin-bottom: 0.1em;
  text-align: center;
  display: block;
}
.event-desc-main {
  font-size: 0.92rem;
  color: #e0e0e0;
  max-height: 2.7em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mapbox-map-wrapper {
  position: relative;
  width: 100%;
  max-width: 1000px;
  margin: 0 auto 2.5rem auto;
}
.mapbox-map {
  position: relative;
  z-index: 1;
  overflow: visible;
}
.map-controls {
  position: absolute;
  top: 1em;
  right: 1em;
  z-index: 5;
  display: flex;
  flex-direction: column;
  gap: 0.5em;
  animation: fadeIn 1.1s;
}

.fit-to-route-btn, .heatmap-toggle-btn {
  display: flex;
  align-items: center;
  gap: 0.5em;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.98rem;
  padding: 0.75em 1.5em;
  cursor: pointer;
  transition: background 0.18s, box-shadow 0.18s, transform 0.18s;
  outline: none;
}

.fit-to-route-btn {
  background: linear-gradient(135deg, #ffb347 0%, #ffcc33 100%);
  box-shadow: 0 4px 15px rgba(255, 204, 51, 0.18);
}

.heatmap-toggle-btn {
  background: linear-gradient(135deg, #4a90e2 0%, #357abd 100%);
  box-shadow: 0 4px 15px rgba(74, 144, 226, 0.18);
}
.fit-to-route-btn:hover, .fit-to-route-btn:focus {
  background: linear-gradient(135deg, #ffcc33 0%, #ffb347 100%);
  box-shadow: 0 6px 24px rgba(255, 204, 51, 0.22);
  transform: translateY(-2px) scale(1.04);
}

.heatmap-toggle-btn:hover, .heatmap-toggle-btn:focus {
  background: linear-gradient(135deg, #357abd 0%, #4a90e2 100%);
  box-shadow: 0 6px 24px rgba(74, 144, 226, 0.22);
  transform: translateY(-2px) scale(1.04);
}
.fit-to-route-btn svg {
  display: block;
}
.mapboxgl-ctrl { display: none; }
.mapboxgl-control-container { display: none !important; }
.mapbox-map .mapboxgl-control-container {
  display: none !important;
}
.dashboard-link-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4em;
  background: linear-gradient(135deg, #357abd 0%, #4a90e2 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.98rem;
  padding: 0.55em 1.2em;
  box-shadow: 0 2px 8px rgba(53, 122, 189, 0.13);
  cursor: pointer;
  text-decoration: none;
  margin-top: 0.7em;
  transition: background 0.18s, box-shadow 0.18s, transform 0.18s;
  justify-content: center;
  margin-left: auto;
  margin-right: auto;
}
.dashboard-link-btn:hover, .dashboard-link-btn:focus {
  background: linear-gradient(135deg, #4a90e2 0%, #357abd 100%);
  box-shadow: 0 4px 16px rgba(53, 122, 189, 0.18);
  transform: translateY(-1px) scale(1.03);
}
@media (max-width: 900px) {
  .event-info-card, .mapbox-map, .map-action-bar {
    max-width: 100%;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
  .overlay-info {
    bottom: 2vw;
    right: 2vw;
    left: auto;
    top: auto;
    max-width: 98vw;
    min-width: 0;
    padding: 0.7rem 0.7rem;
  }
  .overlay-controls {
    top: 2vw;
    right: 2vw;
    left: auto;
    padding: 0.7rem 0.7rem;
  }
  .mapbox-map {
    max-width: 100vw;
    height: 60vh;
    min-height: 260px;
  }
  .event-info-static {
    max-width: 98vw;
    padding: 0.9em 1.3em 0.9em 0.8em;
    border-radius: 14px;
    margin: 1.2rem auto 0.7rem auto;
  }
  .event-name-main {
    font-size: 1.05rem;
  }
  .event-date-main {
    font-size: 0.82rem;
  }
  .map-controls {
    top: 0.7em;
    right: 0.7em;
  }

  .fit-to-route-btn, .heatmap-toggle-btn {
    font-size: 0.92rem;
    padding: 0.6em 1.1em;
  }
}
@media (max-width: 600px) {
  .event-info-card {
    padding: 1rem 0.5rem;
    border-radius: 10px;
    margin: 1rem 0.2rem 1rem 0.2rem;
  }
  .event-info-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.7rem;
  }
  .event-image {
    width: 60px;
    height: 60px;
    border-radius: 8px;
  }
  .event-title {
    font-size: 1.2rem;
  }
  .event-date {
    font-size: 0.95rem;
  }
  .event-description {
    font-size: 0.95rem;
  }
  .floating-event-info {
    bottom: 0.7em;
    right: 0.7em;
    min-width: 120px;
    max-width: 95vw;
    padding: 0.6em 1em 0.6em 0.7em;
    border-radius: 8px;
  }
  .event-name-main {
    font-size: 0.78rem; /* even smaller for mobile */
    line-height: 1.05;
    margin-bottom: 0.02em;
    padding: 0;
    word-break: break-word;
    white-space: normal;
  }
  .compact-event-name {
    font-size: 0.74rem !important; /* even more compact */
    font-weight: 600;
    margin-bottom: 0;
    padding: 0;
    letter-spacing: 0.01em;
    max-width: 90vw;
    word-break: break-word;
    white-space: normal;
  }
  .event-date-main {
    font-size: 0.75rem;
    text-align: center !important;
    display: block !important;
    width: 100%;
  }
  .compact-event-date {
    font-size: 0.68rem !important;
    margin-top: 0.1em;
    color: #b0c4de;
    text-align: center !important;
    display: block !important;
    width: 100%;
  }
  .event-desc-main {
    font-size: 0.8rem;
    max-width: 90vw;
  }
  .mapbox-map {
    height: 55vh;
    min-height: 200px;
    border-radius: 8px;
    margin-bottom: 1.2rem;
    max-width: 100vw;
  }
  .overlay-info {
    bottom: 1vw;
    right: 1vw;
    left: auto;
    top: auto;
    max-width: 97vw;
    min-width: 0;
    padding: 0.5rem 0.5rem;
    border-radius: 10px;
  }
  .overlay-controls {
    top: 1vw;
    right: 1vw;
    left: auto;
    padding: 0.5rem 0.5rem;
    border-radius: 10px;
  }
  .map-action-bar {
    padding: 0 0.2rem;
  }
  .map-controls {
    top: 0.4em;
    right: 0.4em;
  }

  .fit-to-route-btn, .heatmap-toggle-btn {
    font-size: 0.65rem;
    padding: 0.25em 0.5em;
  }
}
.compact-event-info {
  padding: 0.7em 1.2em 0.7em 1em !important;
  margin: 1.1rem auto 0.5rem auto !important;
  gap: 0.15em !important;
  border-radius: 12px !important;
}
.event-info-row {
  display: flex;
  align-items: center;
  gap: 0.7em;
  width: 100%;
  justify-content: center;
}
.compact-event-name {
  font-size: 1.02rem !important;
  font-weight: 700;
  margin-bottom: 0;
}
.compact-event-date {
  font-size: 0.78rem !important;
  margin-top: 0.1em;
  color: #b0c4de;
  text-align: center;
  display: block;
}
.compact-dashboard-btn {
  font-size: 1.1rem !important;
  padding: 0.22em 0.5em !important;
  margin-top: 0 !important;
  border-radius: 6px !important;
  box-shadow: 0 1px 4px rgba(53, 122, 189, 0.10) !important;
  min-width: 2.1em;
  min-height: 2.1em;
  justify-content: center;
}
.dashboard-icon {
  color: #fff;
  font-size: 1.2em;
  vertical-align: middle;
}
@media (max-width: 600px) {
  .event-info-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.3em;
  }
  .compact-event-info {
    padding: 0.5em 0.6em 0.5em 0.5em !important;
    border-radius: 8px !important;
  }
  .compact-dashboard-btn {
    font-size: 0.82rem !important;
    padding: 0.22em 0.6em !important;
    border-radius: 5px !important;
  }
}

/* Polyline popup styles */
.polyline-popup {
  z-index: 1000;
}

.polyline-popup .mapboxgl-popup-content {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 0.9rem;
  font-weight: 500;
  color: #234;
  border: 1px solid #e0e0e0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(4px);
}

.polyline-popup .mapboxgl-popup-tip {
  border-top-color: rgba(255, 255, 255, 0.95);
}

.athlete-popup {
  white-space: nowrap;
  font-family: inherit;
}

.athlete-popup strong {
  color: #4a90e2;
  font-weight: 600;
}
</style>
